export const MAIN_TAB_PATHS = ["/", "/cash-flow", "/ledger", "/investment", "/settings"] as const;

export type MainTabPath = (typeof MAIN_TAB_PATHS)[number];

export const APP_SCROLL_ROOT_ID = "app-scroll-root";

export function isMainTabPath(path: string): path is MainTabPath {
  return (MAIN_TAB_PATHS as readonly string[]).includes(path);
}

export function getAppScrollRoot(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.getElementById(APP_SCROLL_ROOT_ID);
}

function readScrollTop(): number {
  const appRoot = getAppScrollRoot();
  return appRoot ? appRoot.scrollTop : window.scrollY;
}

function writeScrollTop(top: number, behavior: ScrollBehavior = "auto") {
  const appRoot = getAppScrollRoot();
  if (appRoot) {
    appRoot.scrollTo({ top, left: 0, behavior });
    return;
  }
  window.scrollTo({ top, left: 0, behavior });
}

/** Stop iOS momentum / smooth scroll on an overflow container. */
function stopContainerScrollMomentum(el: HTMLElement) {
  const prevOverflow = el.style.overflow;
  el.style.overflow = "hidden";
  el.scrollTop = el.scrollTop;
  el.scrollTop = 0;
  void el.offsetHeight;
  el.style.overflow = prevOverflow || "";
}

export function resetBodyScrollLock() {
  if (typeof document === "undefined") return;

  const body = document.body;
  const html = document.documentElement;

  body.removeAttribute("data-scroll-locked");
  body.classList.remove("overflow-hidden");

  for (const el of [body, html]) {
    el.style.removeProperty("position");
    el.style.removeProperty("top");
    el.style.removeProperty("left");
    el.style.removeProperty("right");
    el.style.removeProperty("width");
    el.style.removeProperty("overflow");
    el.style.removeProperty("padding-right");
    el.style.removeProperty("margin-right");
    el.style.removeProperty("pointer-events");
    el.style.removeProperty("transform");
  }

  html.style.overflow = "hidden";
  body.style.overflow = "hidden";
}

export function releaseBodyScrollLock(): number {
  if (typeof document === "undefined") return 0;

  const body = document.body;
  let scrollY = readScrollTop();

  if (body.style.position === "fixed") {
    const parsedTop = parseInt(body.style.top || "0", 10);
    scrollY = Number.isFinite(parsedTop) ? Math.abs(parsedTop) : scrollY;
  }

  resetBodyScrollLock();
  writeScrollTop(scrollY, "auto");

  return scrollY;
}

function stopSmoothScrollAnimation() {
  const appRoot = getAppScrollRoot();
  if (appRoot) {
    stopContainerScrollMomentum(appRoot);
  }
  const scrollRoot = document.scrollingElement ?? document.documentElement;
  if (scrollRoot) {
    scrollRoot.scrollTop = scrollRoot.scrollTop;
  }
}

let ledgerBudgetDetailScrollTimer: ReturnType<typeof setTimeout> | null = null;
let ledgerBudgetDetailScrollFollowUpTimer: ReturnType<typeof setTimeout> | null = null;

export function cancelLedgerBudgetDetailScroll(options?: { preserveScroll?: boolean }) {
  if (ledgerBudgetDetailScrollTimer !== null) {
    clearTimeout(ledgerBudgetDetailScrollTimer);
    ledgerBudgetDetailScrollTimer = null;
  }
  if (ledgerBudgetDetailScrollFollowUpTimer !== null) {
    clearTimeout(ledgerBudgetDetailScrollFollowUpTimer);
    ledgerBudgetDetailScrollFollowUpTimer = null;
  }
  if (!options?.preserveScroll) {
    stopSmoothScrollAnimation();
  }
}

export function scrollElementInAppRoot(
  element: HTMLElement,
  options: { headerOffset?: number; behavior?: ScrollBehavior } = {},
) {
  const { headerOffset = 56, behavior = "smooth" } = options;
  resetBodyScrollLock();

  const appRoot = getAppScrollRoot();
  if (!appRoot) {
    element.scrollIntoView({ behavior, block: "start" });
    return;
  }

  const rootRect = appRoot.getBoundingClientRect();
  const elRect = element.getBoundingClientRect();
  const top = appRoot.scrollTop + (elRect.top - rootRect.top) - headerOffset;

  appRoot.scrollTo({ top: Math.max(0, top), behavior });
}

export function scheduleLedgerBudgetDetailScroll(
  getElement: () => HTMLElement | null,
  headerOffset = 56,
) {
  cancelLedgerBudgetDetailScroll();

  ledgerBudgetDetailScrollTimer = window.setTimeout(() => {
    ledgerBudgetDetailScrollTimer = null;
    resetBodyScrollLock();

    const appRoot = getAppScrollRoot();
    if (appRoot) {
      stopContainerScrollMomentum(appRoot);
    } else {
      writeScrollTop(0, "auto");
    }

    ledgerBudgetDetailScrollFollowUpTimer = window.setTimeout(() => {
      ledgerBudgetDetailScrollFollowUpTimer = null;

      const runScroll = (attempt = 0) => {
        const element = getElement();
        if (!element) {
          if (attempt < 10) {
            window.setTimeout(() => runScroll(attempt + 1), 50);
          }
          return;
        }

        const prefersReducedMotion =
          typeof window !== "undefined" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const useSmooth = !prefersReducedMotion;

        scrollElementInAppRoot(element, {
          headerOffset,
          behavior: useSmooth ? "smooth" : "auto",
        });
      };

      runScroll();
    }, 80);
  }, 280);
}

export function forceScrollToTop() {
  cancelLedgerBudgetDetailScroll();
  resetBodyScrollLock();
  stopSmoothScrollAnimation();

  const appRoot = getAppScrollRoot();
  if (appRoot) {
    stopContainerScrollMomentum(appRoot);
  }

  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

export function scrollAppToTop() {
  forceScrollToTop();
}

export function scheduleScrollAppToTop() {
  const delays = [0, 50, 150, 300, 500];
  for (const delay of delays) {
    window.setTimeout(forceScrollToTop, delay);
  }
}

export function scrollMainTabToTopOnNavClick() {
  forceScrollToTop();
  scheduleScrollAppToTop();
}

export function initManualScrollRestoration() {
  if (typeof window === "undefined" || !("scrollRestoration" in window.history)) return;
  try {
    window.history.scrollRestoration = "manual";
  } catch {
    // ignore
  }
}

export function clearLedgerReturnFocusSession() {
  sessionStorage.removeItem("ledger.returnFocusEntryId");
  sessionStorage.removeItem("ledger.returnFocusTs");
}
